<template>
    <div class="card mt-3">
        <div class="card-header pb-3">
            <div class="d-flex justify-content-between align-items-center">
                <div>
                    <span class="h4">Blok renders</span>
                </div>
            </div>
        </div>
        <div class="card-body pt-0">
            <div class="mb-4">
                <input
                    type="text"
                    placeholder="Zoeken..."
                    class="form-control"
                    v-model="search"
                    @keydown.enter="loadBlocks(1)"
                    @blur="loadBlocks(1)"
                />
            </div>

            <table class="table table-sm table-no-side-paddings table-striped" style="table-layout: fixed">
                <thead>
                    <tr>
                        <Ordering @change="loadBlocks" v-model="order" field="id" tag="th">
                            #
                        </Ordering>
                        <Ordering @change="loadBlocks" v-model="order" field="ceremony_code" tag="th">
                            Ceremonie
                        </Ordering>
                        <Ordering
                            @change="loadBlocks"
                            v-model="order"
                            field="company_name"
                            tag="th"
                        >
                            Klant
                        </Ordering>
                        <Ordering
                            @change="loadBlocks"
                            v-model="order"
                            field="name"
                            tag="th"
                        >
                            Naam
                        </Ordering>
                        <Ordering
                            @change="loadBlocks"
                            v-model="order"
                            field="type"
                            tag="th"
                        >
                            Soort
                        </Ordering>
                        <Ordering
                            @change="loadBlocks"
                            v-model="order"
                            field="render_state"
                            class="text-right"
                            tag="th"
                        >
                            Render status
                        </Ordering>
                        <Ordering
                            @change="loadBlocks"
                            v-model="order"
                            field="render_progress"
                            class="text-right"
                            tag="th"
                        >
                            Render percentage
                        </Ordering>
                        <Ordering
                            @change="loadBlocks"
                            v-model="order"
                            field="render_created_at"
                            class="text-right"
                            tag="th"
                        >
                            Render bijgewerkt op
                        </Ordering>
                        <Ordering
                            @change="loadBlocks"
                            v-model="order"
                            field="render_requested_at"
                            class="text-right"
                            tag="th"
                        >
                            Render aangevraagd op
                        </Ordering>
                        <Ordering
                            @change="loadBlocks"
                            v-model="order"
                            field="created_at"
                            class="text-right"
                            tag="th"
                        >
                            Blok aangemaakt op
                        </Ordering>
                        <th />
                    </tr>
                </thead>

                <tbody v-if="blocks?.data?.length">
                    <tr v-for="block in blocks.data" :key="block.id">
                        <td>
                            #{{ block.id }}

                            <span
                                v-if="!block.ceremony.company.block_rendering_enabled"
                                v-tooltip="'Let op: instelling op bedrijf (block_rendering_enabled) mist. Hierdoor wordt dit blok niet automatisch gerenderd.'"
                                class="ml-1 badge badge-danger"
                            >
                                <icon name="exclamation" scale="0.8" class="m-0" style="fill: #fff"/>
                            </span>
                        </td>
                        <td class="text-wrap">
                            <RouterLink :to="'/code/' + block.ceremony.code">{{ block.ceremony.name }}</RouterLink>
                            <div>
                                <RouterLink :to="'/code/' + block.ceremony.code">
                                    <small>{{ block.ceremony.code }}</small>
                                </RouterLink>
                            </div>
                        </td>
                        <td class="text-wrap">
                            {{ block.ceremony.company.name }}
                        </td>
                        <td class="text-wrap">
                            {{ block.name }}
                        </td>
                        <td>
                            <span class="badge badge-info d-inline-block" v-if="block.type === BLOCK_TYPES.INLOOP_UITLOOP">Inloop/uitloop</span>
                            <span class="badge badge-info d-inline-block" v-else-if="block.type === BLOCK_TYPES.CONDOLENCE">Condoleance</span>
                            <span class="badge badge-info d-inline-block" v-else-if="block.type === BLOCK_TYPES.MEDIA">Media</span>
                            <span class="badge badge-dark d-inline-block" v-else-if="block.type === BLOCK_TYPES.SPEAKER">Spreker</span>
                            <span class="badge badge-dark d-inline-block" v-else-if="block.type === BLOCK_TYPES.CALL">Inbellen</span>
                        </td>
                        <td class="text-right">
                            <span class="badge badge-secondary d-inline-block" v-if="block.render_state === BlockRenderState.Pending">Pending</span>
                            <span class="badge badge-info d-inline-block" v-else-if="block.render_state === BlockRenderState.Queued">Queued</span>
                            <span class="badge badge-primary d-inline-block" v-else-if="block.render_state === BlockRenderState.Rendering">Rendering</span>
                            <span class="badge badge-danger d-inline-block" v-else-if="block.render_state === BlockRenderState.Failed">Failed</span>
                            <span class="badge badge-success d-inline-block" v-else-if="block.render_state === BlockRenderState.Ready">Ready</span>
                            <span class="badge badge-warning d-inline-block" v-else-if="block.render_state === BlockRenderState.NoContent">No content</span>

                            <span
                                v-if="block.render_state === BlockRenderState.Ready && block.render_created_at && block.render_created_at < block.render_requested_at"
                                v-tooltip="'Let op: huidige blok render is out-of-date, deze zal binnenkort worden opgepakt voor een nieuwe render'"
                                class="ml-1 badge badge-warning d-inline-block"
                            >
                                <icon name="exclamation" scale="0.8" class="m-0" style="fill: #fff"/>
                            </span>
                        </td>
                        <td class="text-right">{{ block.render_progress }}%</td>
                        <td class="text-right">{{ block.render_created_at ? formatDateTime(block.render_created_at) : '-' }}</td>
                        <td class="text-right">{{ block.render_requested_at ? formatDateTime(block.render_requested_at) : '-' }}</td>
                        <td class="text-right">{{ formatDateTime(block.created_at) }}</td>
                        <td class="text-right">
                            <a
                                v-if="block.render_url"
                                :href="block.render_url"
                                target="_blank"
                                class="btn btn-xs rounded-pill btn-success ml-auto"
                            >
                                <icon name="download" scale="0.8"></icon>
                            </a>

                            <button class="btn btn-xs rounded-pill btn-primary"
                                    v-if="block.render_state !== BlockRenderState.Rendering"
                                    @click="addBlockToQueue(block)"
                                    v-tooltip="'Voeg blok toe aan de render queue (opent geen bevestiging)'">
                                <icon name="microchip" scale="0.8"></icon>
                            </button>
                        </td>
                    </tr>
                </tbody>

                <tbody v-if="!isLoading && !blocks?.data?.length">
                    <tr>
                        <td colspan="100%">
                            Geen blokken gevonden
                        </td>
                    </tr>
                </tbody>
            </table>

            <Pagination v-if="blocks" :pagination="blocks" @change="loadBlocks" />
        </div>
    </div>
</template>

<script setup>
    import Pagination from '../../../components/Pagination.vue';
    import Ordering from '../../../components/Ordering.vue';
    import { inject, onMounted, ref } from 'vue';
    import { notifyError, notifySuccess } from '../../../utils/notify';
    import { BLOCK_TYPES } from '../../../player-v2/enums.js';
    import { BlockRenderState } from '../../../enums/block-render-state.js';
    import { formatDateTime } from '../../../utils/formatDate.js';

    const store = inject('store');
    const http = inject('http');

    const isLoading = ref(false);
    const order = ref('created_at,desc');
    const search = ref(null);
    const blocks = ref(null);

    onMounted(() => {
        const params = new URLSearchParams(window.location.search);

        order.value = params.get('order') || 'created_at,desc';
        search.value = params.get('search');

        loadBlocks(params.get('page'));
    });

    async function loadBlocks(pageNumber = null) {
        isLoading.value = true;

        try {
            const page = pageNumber || blocks.value?.current_page || 1;

            const params = new URLSearchParams();

            params.append('order', order.value);
            params.append('page', page);
            if (search.value?.length) {
                params.append('search', search.value);
            }

            const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
            window.history.pushState({}, '', newUrl);

            const response = await http.get('/api/admin/block/renders', {
                params: {
                    page,
                    search: search.value,
                    order: order.value,
                },
            });

            blocks.value = response.data;
        } catch (exception) {
            notifyError(exception);
        } finally {
            isLoading.value = false;
        }
    }

    async function addBlockToQueue(block) {
        isLoading.value = true;

        try {
            await http.post('/api/admin/block/renders/' + block.id + '/add-to-render-queue');
            await loadBlocks();
            notifySuccess('Blok toegevoegd aan de render queue');
        } catch (exception) {
            notifyError(exception);
        } finally {
            isLoading.value = false;
        }
    }
</script>
